/**
 * Thrivin Journal – Blog & Article JS
 * Handles: Load More (AJAX), Reading Progress, TOC Builder, Active Scroll
 */

(function () {
  'use strict';

  /* ── LOAD MORE ─────────────────────────────────────────── */
  const loadMoreBtn = document.getElementById('tj-load-more');
  const grid = document.getElementById('tj-articles-grid');

  if (loadMoreBtn && grid) {
    loadMoreBtn.addEventListener('click', async function () {
      const url = this.dataset.url;
      if (!url) return;

      this.textContent = 'Loading…';
      this.disabled = true;

      try {
        const res = await fetch(url);
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newCards = doc.querySelectorAll('#tj-articles-grid .tj-card');
        const nextBtn = doc.getElementById('tj-load-more');

        newCards.forEach((card, i) => {
          card.classList.add('is-loading');
          card.style.animationDelay = (i * 0.08) + 's';
          grid.appendChild(card);
        });

        if (nextBtn) {
          loadMoreBtn.dataset.url = nextBtn.dataset.url;
          loadMoreBtn.textContent = 'Load more articles';
          loadMoreBtn.disabled = false;
        } else {
          const wrap = document.getElementById('tj-load-more-wrap');
          if (wrap) wrap.remove();
        }
      } catch (err) {
        loadMoreBtn.textContent = 'Load more articles';
        loadMoreBtn.disabled = false;
      }
    });
  }

  /* ── READING PROGRESS BAR ───────────────────────────────── */
  const progressBar = document.getElementById('tj-progress-bar');

  if (progressBar) {
    const articleContent = document.getElementById('tj-article-content');
    if (articleContent) {
      function updateProgress() {
        const winTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const pct = Math.min(100, Math.round((winTop / docHeight) * 100));
        progressBar.style.width = pct + '%';
        progressBar.setAttribute('aria-valuenow', pct);
      }
      window.addEventListener('scroll', updateProgress, { passive: true });
    }
  }

  /* ── TABLE OF CONTENTS ─────────────────────────────────── */
  function buildTOC(navEl) {
    if (!navEl) return;
    const content = document.getElementById('tj-article-content');
    if (!content) return;

    const headings = content.querySelectorAll('h2, h3');
    if (headings.length === 0) {
      const container = navEl.closest('.tj-toc');
      if (container) container.style.display = 'none';
      return;
    }

    headings.forEach((h, i) => {
      if (!h.id) h.id = 'tj-heading-' + i;
      const a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      a.dataset.target = h.id;
      if (h.tagName === 'H3') a.classList.add('tj-toc-h3');
      a.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.getElementById(this.dataset.target);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
      navEl.appendChild(a);
    });
  }

  // Desktop TOC
  const tocNav = document.getElementById('tj-toc-list');
  buildTOC(tocNav);

  // Mobile TOC – clone links
  const tocNavMobile = document.getElementById('tj-toc-list-mobile');
  if (tocNavMobile && tocNav) {
    tocNav.querySelectorAll('a').forEach(a => {
      tocNavMobile.appendChild(a.cloneNode(true));
    });
    tocNavMobile.querySelectorAll('a').forEach(a => {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.getElementById(this.dataset.target);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* TOC toggles */
  ['tj-toc-toggle', 'tj-toc-toggle-mobile'].forEach(id => {
    const btn = document.getElementById(id);
    if (!btn) return;
    btn.addEventListener('click', function () {
      const expanded = this.getAttribute('aria-expanded') === 'true';
      this.setAttribute('aria-expanded', !expanded);
      const targetId = this.getAttribute('aria-controls');
      const nav = document.getElementById(targetId);
      if (nav) {
        if (expanded) {
          nav.hidden = true;
        } else {
          nav.hidden = false;
        }
      }
    });
  });

  /* ── ACTIVE TOC HIGHLIGHT ───────────────────────────────── */
  function setupActiveHeadings(navEl) {
    if (!navEl) return;
    const content = document.getElementById('tj-article-content');
    if (!content) return;
    const headings = Array.from(content.querySelectorAll('h2, h3'));
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            navEl.querySelectorAll('a').forEach(a => a.classList.remove('active'));
            const link = navEl.querySelector('[data-target="' + entry.target.id + '"]');
            if (link) link.classList.add('active');
          }
        });
      },
      { rootMargin: '0px 0px -60% 0px', threshold: 0 }
    );

    headings.forEach(h => observer.observe(h));
  }

  setupActiveHeadings(tocNav);
  setupActiveHeadings(tocNavMobile);

  /* ── COPY LINK BUTTONS ──────────────────────────────────── */
  document.querySelectorAll('.tj-copy-btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const url = this.dataset.copyUrl || window.location.href;

      navigator.clipboard.writeText(url).then(function () {
        showCopyTooltip(btn);
      }).catch(function () {
        // Fallback for older browsers
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        showCopyTooltip(btn);
      });
    });
  });

  function showCopyTooltip(btn) {
    // Prevent stacking if already shown
    if (btn.dataset.copying) return;
    btn.dataset.copying = '1';

    const tip = document.createElement('span');
    tip.className = 'tj-copy-tooltip';
    tip.textContent = 'Copied!';
    tip.setAttribute('role', 'status');
    tip.setAttribute('aria-live', 'polite');
    btn.style.position = 'relative';
    btn.appendChild(tip);

    // Trigger animation
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { tip.classList.add('tj-copy-tooltip--visible'); });
    });

    setTimeout(function () {
      tip.classList.remove('tj-copy-tooltip--visible');
      setTimeout(function () {
        tip.remove();
        delete btn.dataset.copying;
      }, 200);
    }, 1800);
  }

})();
